import React from 'react';
import { Breakpoint } from 'types/utils';
import { jcm } from 'utils';
import { PictureSources } from '../photos-modal.types';
import { thumbsScrollerDesktop } from './modal-layout-desktops.styles';
import { thumbsScroller, thumbsScrollerMobile } from './modal-layout-touch.styles';
import { PictureThumbAtDesktop } from './modal-thumb-at-desktop';
import { PictureThumbAtTouch } from './modal-thumb-at-touch';
import {
  adjustScrollHorizontal,
  adjustScrollVertical,
  isScrolledNearEnd,
  SCROLL_DEBOUNCE_MS,
} from './utils';

export const ModalThumbs: React.FC<{
  showedPicIdx: number;
  photosThumbs: PictureSources[];
  breakpoint: Breakpoint;
  hasMorePhotos?: boolean;
  isCheckedGallery?: boolean;
  onRequestMorePhotos?: () => void;
  setShowedPicture?: (value: PictureSources | null) => void;
}> = ({
  showedPicIdx,
  photosThumbs,
  breakpoint,
  setShowedPicture,
  hasMorePhotos = false,
  isCheckedGallery = false,
  onRequestMorePhotos = () => null,
}) => {
  const activeThumbRef = React.useRef<HTMLButtonElement>(null);
  const thumbsScrollerRef = React.useRef<HTMLDivElement>(null);

  const requestPendingRef = React.useRef(false);
  const lastThumbsCountRef = React.useRef(photosThumbs.length);
  const scrollDebounceRef = React.useRef<number | null>(null);

  const atDesktop = breakpoint === 'large' || breakpoint === 'medium';
  const PictureThumb = atDesktop ? PictureThumbAtDesktop : PictureThumbAtTouch;

  const thumbs = React.useMemo(
    () =>
      photosThumbs.map((pic, idx) => (
        <PictureThumb
          key={pic.urlOriginal}
          activeThumbRef={activeThumbRef}
          thumbPicture={pic}
          isActive={idx === showedPicIdx}
          setShowedPicture={setShowedPicture}
          breakpoint={breakpoint}
        />
      )),
    [PictureThumb, photosThumbs, activeThumbRef, showedPicIdx, setShowedPicture, breakpoint],
  );

  React.useEffect(() => {
    adjustScroll(atDesktop ? 'vertical' : 'horizontal', thumbsScrollerRef.current, activeThumbRef.current);
  }, [showedPicIdx, atDesktop]);

  React.useEffect(() => {
    if (photosThumbs.length > lastThumbsCountRef.current) {
      requestPendingRef.current = false;
      lastThumbsCountRef.current = photosThumbs.length;
    }
    if (!hasMorePhotos) {
      requestPendingRef.current = false;
    }
  }, [photosThumbs.length, hasMorePhotos]);

  const handleThumbsScroll = () => {
    const node = thumbsScrollerRef.current;
    if (!node) return;

    if (scrollDebounceRef.current) {
      window.clearTimeout(scrollDebounceRef.current);
    }

    scrollDebounceRef.current = window.setTimeout(() => {
      if (!isCheckedGallery || !hasMorePhotos || requestPendingRef.current) return;
      if (!isScrolledNearEnd(node, atDesktop)) return;
      requestPendingRef.current = true;
      onRequestMorePhotos();
    }, SCROLL_DEBOUNCE_MS);
  };

  return (
    <div
      className={jcm({
        [thumbsScrollerDesktop]: atDesktop,
        [thumbsScroller]: !atDesktop,
        [thumbsScrollerMobile]: !atDesktop && breakpoint === 'mobile',
      })}
      ref={thumbsScrollerRef}
      role="tablist"
      onScroll={handleThumbsScroll}
    >
      {thumbs}
    </div>
  );
};

export const adjustScroll = (
  strategy: 'vertical' | 'horizontal',
  scrollContainer: HTMLElement | null,
  itemElement: HTMLElement | null,
) => {
  if (scrollContainer instanceof HTMLElement && itemElement instanceof HTMLElement) {
    const containerRect = scrollContainer.getBoundingClientRect();
    const itemRect = itemElement.getBoundingClientRect();

    if (strategy === 'vertical') {
      const containerScrollTop = scrollContainer.scrollTop;
      adjustScrollVertical(containerRect, itemRect, containerScrollTop, (scrollToOptions) =>
        scrollContainer.scrollTo(scrollToOptions),
      );
    } else {
      const containerScrollLeft = scrollContainer.scrollLeft;
      adjustScrollHorizontal(containerRect, itemRect, containerScrollLeft, (scrollToOptions) =>
        scrollContainer.scrollTo(scrollToOptions),
      );
    }
  }
};
